import { useLocation } from '@marketplace-web/shared/browser'
import { useDebounce, useLatestCallback } from '@marketplace-web/shared/ui-helpers'

import { toParams, toUrlQuery } from '@marketplace-web/shared/utils'
import { CATALOG_URL } from 'constants/routes'
import { getCatalogFilters } from 'data/api/get-catalog-filters'
import {
  transformDynamicFilters,
  transformSelectedDynamicFilters,
} from 'data/transformers/dynamic-filter'
import { useBackButtonAbTest } from '@marketplace-web/domain/search'
import {
  DynamicFilterReducerAction,
  FilterReducerAction,
  GeneralReducerAction,
} from 'pages/Catalog/CatalogProvider/reducers/actionTypes'
import { PriceRange } from 'pages/Catalog/CatalogProvider/types'
import useCatalogContext from 'pages/Catalog/CatalogProvider/useCatalogContext'
import { buildCatalogUrlParams } from 'pages/Catalog/utils/utils'
import { GetCatalogFiltersArgs } from 'types/api/arguments/get-catalog-filters-args'
import { FilterModel } from 'types/models/filter'
import { SelectedDynamicFilterModel } from 'types/models/dynamic-filter'

import { CATALOG_FILTER_CHANGE_DEBOUNCE_AMOUNT } from '../constants'

type FetchDynamicFiltersParams = {
  selectedDynamicFilters: Array<SelectedDynamicFilterModel>
  selectedFilters?: Partial<FilterModel>
  isTemporary?: boolean
  selectedTemporaryCatalogIds?: Array<number>
  selectedTemporaryPriceRange?: PriceRange
  isSearchTextOnly?: boolean
}

const useFetchDynamicFilters = () => {
  const { state, dispatch, refs } = useCatalogContext()
  const {
    urlParams,
    searchParams: { search_text },
  } = useLocation()
  const searchQuery = search_text || ''

  const { historyMethod } = useBackButtonAbTest()

  const debouncedRequest = useDebounce(
    (
      payload: GetCatalogFiltersArgs,
      isTemporary: boolean,
      updatedUrl: string,
      requestVersion: number,
      isSearchTextOnly: boolean,
    ) => {
      dispatch({
        type: DynamicFilterReducerAction.FETCH_DYNAMIC_FILTERS_REQUEST,
        payload: undefined,
      })

      getCatalogFilters(payload).then(response => {
        if (requestVersion !== refs.dynamicFilterRequestVersion?.current) return

        if ('errors' in response) {
          dispatch({
            type: DynamicFilterReducerAction.FETCH_DYNAMIC_FILTERS_FAILURE,
            payload: { errors: response.errors },
          })
        } else if (isTemporary) {
          dispatch({
            type: DynamicFilterReducerAction.FETCH_TEMPORARY_DYNAMIC_FILTERS_SUCCESS,
            payload: {
              dynamicFilters: transformDynamicFilters(response.filters),
              temporarySelectedDynamicFilters: transformSelectedDynamicFilters(
                response.selected_filters,
              ),
            },
          })
        } else {
          dispatch({
            type: DynamicFilterReducerAction.FETCH_DYNAMIC_FILTERS_SUCCESS,
            payload: {
              dynamicFilters: transformDynamicFilters(response.filters),
              selectedDynamicFilters: transformSelectedDynamicFilters(response.selected_filters),
            },
          })

          dispatch({
            type: GeneralReducerAction.INCREMENT_FILTERS_CHANGE_COUNT,
            payload: undefined,
          })

          if (isSearchTextOnly || historyMethod === 'replaceState') {
            // search text is mostly updated with pushState, so pushing it again
            // would mean duplicate history stack entries
            window.history.replaceState(undefined, '', updatedUrl)
          } else {
            window.history.pushState(undefined, '', updatedUrl)
          }
        }
      })
    },
    CATALOG_FILTER_CHANGE_DEBOUNCE_AMOUNT,
    false,
  )

  const fetchDynamicFilters = useLatestCallback(
    ({
      selectedDynamicFilters,
      selectedFilters,
      isTemporary = false,
      selectedTemporaryCatalogIds,
      selectedTemporaryPriceRange,
      isSearchTextOnly,
    }: FetchDynamicFiltersParams) => {
      let payload: GetCatalogFiltersArgs
      const { filterSearchQuery, temporaryPriceRange, temporaryCatalogIds } = state.dynamicFilters
      const filters = {
        ...state.filters,
        ...selectedFilters,
        query: String(searchQuery),
      }
      const time = urlParams.time && Number(urlParams.time)

      dispatch({
        type: FilterReducerAction.CHANGE_FILTERS,
        payload: {
          filters,
        },
      })

      if (isTemporary) {
        const currentTemporaryCatalogIds = selectedTemporaryCatalogIds ?? temporaryCatalogIds
        const currentTemporaryPriceRange = selectedTemporaryPriceRange ?? temporaryPriceRange

        dispatch({
          type: DynamicFilterReducerAction.SET_TEMPORARY_SELECTED_DYNAMIC_FILTERS,
          payload: { temporarySelectedDynamicFilters: selectedDynamicFilters },
        })

        dispatch({
          type: DynamicFilterReducerAction.SET_TEMPORARY_CATALOG_IDS,
          payload: { temporaryCatalogIds: currentTemporaryCatalogIds },
        })

        dispatch({
          type: DynamicFilterReducerAction.SET_TEMPORARY_PRICE_RANGE,
          payload: { priceRange: currentTemporaryPriceRange },
        })

        payload = {
          ...filters,
          selectedDynamicFilters,
          catalogIds: currentTemporaryCatalogIds,
          priceFrom: currentTemporaryPriceRange.priceFrom,
          priceTo: currentTemporaryPriceRange.priceTo,
          filterSearchText: filterSearchQuery.text,
          filterSearchCode: filterSearchQuery.code,
          time,
        }
      } else {
        dispatch({
          type: DynamicFilterReducerAction.SET_SELECTED_DYNAMIC_FILTERS,
          payload: { selectedDynamicFilters },
        })

        payload = {
          ...filters,
          selectedDynamicFilters,
          filterSearchText: filterSearchQuery.text,
          filterSearchCode: filterSearchQuery.code,
          time,
        }
      }

      const extraParams = toParams(window.location.search)
      const filterParams = buildCatalogUrlParams({
        filters,
        selectedDynamicFilters,
        extraParams,
      })
      const updatedUrl = `${CATALOG_URL}?${toUrlQuery(filterParams)}`

      let requestVersion = 0
      if (refs.dynamicFilterRequestVersion?.current != null) {
        refs.dynamicFilterRequestVersion.current += 1
        requestVersion = refs.dynamicFilterRequestVersion.current
      }

      debouncedRequest(payload, isTemporary, updatedUrl, requestVersion, !!isSearchTextOnly)
    },
  )

  return fetchDynamicFilters
}

export default useFetchDynamicFilters
